import axios from 'axios';
import { getCanonicalDeviceStatus } from './deviceStatus';

const API_BASE =
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_API_URL ||
  'https://app.krush.in/api';

const toFiniteNumber = (value, fallback = null) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const toNullableBoolean = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const lowered = value.trim().toLowerCase();
    if (
      ["1", "true", "on", "yes", "acc_on", "ignition_on", "engine_on", "engine_start", "started"].includes(lowered)
    ) {
      return true;
    }
    if (
      ["0", "false", "off", "no", "acc_off", "ignition_off", "engine_off", "engine_stop", "stopped"].includes(
        lowered
      )
    ) {
      return false;
    }
  }
  return null;
};

const isValidCoordinate = (lat, lng) => {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return false;
  if (lat === 0 && lng === 0) return false;
  return true;
};

const sanitizeText = (value) => {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text ? text : null;
};

const toTimestampValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed =
    typeof value === "number" && Number.isFinite(value)
      ? value
      : new Date(value).getTime();
  if (!Number.isFinite(parsed)) return null;
  return new Date(parsed).toISOString();
};

export const normalizeDeviceLivePayload = (device = {}) => {
  const baseLiveData = {
    ...(device.liveData || {}),
    ...(device.deviceLive || {}),
    ...(device.liveSnapshot || {}),
  };
  const currentLocation = device.currentLocation || {};

  const latitude = toFiniteNumber(
    baseLiveData.latitude ?? baseLiveData.lat ?? currentLocation.latitude,
    null
  );
  const longitude = toFiniteNumber(
    baseLiveData.longitude ?? baseLiveData.lng ?? currentLocation.longitude,
    null
  );
  const hasValidLocation = isValidCoordinate(latitude, longitude);
  const timestamp =
    baseLiveData.correctedTime ||
    baseLiveData.timestamp ||
    currentLocation.timestamp ||
    device.lastDataReceivedAt ||
    null;
  const accStatus = toNullableBoolean(
    baseLiveData.accStatus ??
      baseLiveData.acc ??
      baseLiveData.ignition ??
      currentLocation.accStatus ??
      currentLocation.acc ??
      currentLocation.ignition
  );
  const canonicalStatus = getCanonicalDeviceStatus({
    ...device,
    liveData: {
      ...baseLiveData,
      accStatus,
    },
  });
  const normalizedLiveData = {
    ...baseLiveData,
    latitude: hasValidLocation ? latitude : null,
    longitude: hasValidLocation ? longitude : null,
    lat: hasValidLocation ? latitude : null,
    lng: hasValidLocation ? longitude : null,
    speed: toFiniteNumber(baseLiveData.speed ?? currentLocation.speed, 0),
    heading: toFiniteNumber(baseLiveData.heading ?? baseLiveData.course ?? baseLiveData.bearing ?? currentLocation.heading, 0),
    accStatus,
    acc: accStatus,
    vehicleStatus: canonicalStatus,
    direction: sanitizeText(baseLiveData.direction ?? currentLocation.direction),
    turnType: sanitizeText(baseLiveData.turnType ?? baseLiveData.turn_type ?? currentLocation.turnType),
    timestamp,
    deviceTime: toTimestampValue(baseLiveData.deviceTime ?? device.deviceTime),
    serverTime: toTimestampValue(baseLiveData.serverTime ?? device.serverTime),
    correctedTime: toTimestampValue(baseLiveData.correctedTime ?? device.correctedTime ?? timestamp),
    isDrifted: Boolean(baseLiveData.isDrifted ?? device.isDrifted),
    driftMs: toFiniteNumber(baseLiveData.driftMs ?? device.driftMs, 0),
    driftSeconds: toFiniteNumber(baseLiveData.driftSeconds ?? device.driftSeconds, 0),
  };

  const normalizedCurrentLocation = hasValidLocation
    ? {
        latitude,
        longitude,
        timestamp,
        speed: normalizedLiveData.speed,
        heading: normalizedLiveData.heading,
        accStatus,
        vehicleStatus: canonicalStatus,
        direction: normalizedLiveData.direction,
        turnType: normalizedLiveData.turnType,
      }
    : null;

  return {
    ...device,
    status: canonicalStatus,
    vehicleStatus: canonicalStatus,
    liveData: normalizedLiveData,
    currentLocation: normalizedCurrentLocation,
  };
};

/**
 * Get auth headers with token
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      'x-auth-token': token,
      'Content-Type': 'application/json'
    }
  };
};

/**
 * Fetch all users visible to current user (respects RBAC)
 * @returns {Promise<Array>} - Filtered users array
 */
export const fetchAllUsers = async () => {
  try {
    const response = await axios.get(`${API_BASE}/admin/users`, getAuthHeaders());
    return response.data; // Already filtered by backend
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

/**
 * Fetch users by specific role
 * @param {string} role - Role to filter by
 * @returns {Promise<Array>} - Users with specified role
 */
export const fetchUsersByRole = async (role) => {
  try {
    const response = await axios.get(`${API_BASE}/users/by-role/${role}`, getAuthHeaders());
    return response.data.data || [];
  } catch (error) {
    console.error(`Error fetching ${role} users:`, error);
    return [];
  }
};

/**
 * Fetch all devices visible to current user
 * @param {Object} filters - Optional filters (userId, fields)
 * @returns {Promise<Array>} - Filtered devices array
 */
export const fetchDevicesPage = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") return;
      params.append(key, value);
    });
    
    const url = `${API_BASE}/devices${params.toString() ? '?' + params.toString() : ''}`;
    const response = await axios.get(url, getAuthHeaders());
    const payload = response.data;
    const rawDevices = payload?.data || payload || [];
    const normalizedDevices = Array.isArray(rawDevices)
      ? rawDevices.map(normalizeDeviceLivePayload)
      : [];

    return {
      data: normalizedDevices,
      total: Number(payload?.total || normalizedDevices.length || 0),
      page: Number(payload?.page || filters?.page || 1),
      limit: Number(payload?.limit || filters?.limit || normalizedDevices.length || 0),
      hasMore: Boolean(payload?.hasMore),
    };
  } catch (error) {
    console.error('Error fetching devices:', error);
    throw error;
  }
};

export const fetchAllDevices = async (filters = {}) => {
  const page = await fetchDevicesPage(filters);
  return page.data;
};

export const fetchDeviceLiveData = async (deviceId) => {
  try {
    const response = await axios.get(`${API_BASE}/devices/${deviceId}/live-data`, getAuthHeaders());
    return normalizeDeviceLivePayload(response.data || {});
  } catch (error) {
    console.error('Error fetching device live data:', error);
    throw error;
  }
};

/**
 * Fetch device events
 * @param {string} userId - Optional userId filter
 * @returns {Promise<Array>} - Device events
 */
export const fetchDeviceEvents = async (userId = null) => {
  try {
    const url = userId 
      ? `${API_BASE}/devices/events?userId=${userId}`
      : `${API_BASE}/devices/events`;
    
    const response = await axios.get(url, getAuthHeaders());
    return response.data;
  } catch (error) {
    console.error('Error fetching events:', error);
    throw error;
  }
};

/**
 * Create new user
 * @param {Object} userData - User data
 * @returns {Promise<Object>} - Created user
 */
export const createUser = async (userData) => {
  try {
    const response = await axios.post(
      `${API_BASE}/admin/users`,
      userData,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

/**
 * Update user
 * @param {string} userId - User ID
 * @param {Object} updates - Update data
 * @returns {Promise<Object>} - Updated user
 */
export const updateUser = async (userId, updates) => {
  try {
    const response = await axios.put(
      `${API_BASE}/admin/users/${userId}`,
      updates,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

/**
 * Delete user
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - Response message
 */
export const deleteUser = async (userId) => {
  try {
    const response = await axios.delete(
      `${API_BASE}/admin/users/${userId}`,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

/**
 * Get role-based dropdown options for user creation
 * @param {string} currentUserRole - Current user's role
 * @returns {Array<string>} - Roles that can be created
 */
export const getCreatableRolesForDropdown = (currentUserRole) => {
  const roleMap = {
    'admin': ['Manager', 'HR', 'office_emp', 'field_emp', 'sub_admin', 'group_admin', 'client', 'user', 'viewer'],
    'Manager': ['office_emp', 'field_emp', 'sub_admin', 'group_admin'],
    'HR': [], // HR cannot create
    'office_emp': ['sub_admin', 'group_admin', 'client', 'viewer', 'field_emp'],  // ✅ YE ADD KARO
    'sub_admin': ['group_admin'],
    'group_admin': ['client'],
    'client': ['viewer'],
  };
  
  return roleMap[currentUserRole] || [];
};

/**
 * Check if user can perform an action
 * @param {string} action - Action name (e.g., 'createUser', 'editUser')
 * @param {string} role - User's role
 * @returns {boolean}
 */
export const canPerformAction = (action, role) => {
  const permissions = {
    createUser: ['admin', 'Manager', 'office_emp', 'sub_admin', 'group_admin', 'client'],
    editUser: ['admin', 'Manager', 'office_emp', 'sub_admin', 'group_admin'],
    deleteUser: ['admin', 'Manager', 'office_emp', 'sub_admin'],
    viewUsers: ['admin', 'Manager', 'HR', 'office_emp', 'sub_admin'],
    createDevice: ['admin', 'Manager', 'sub_admin'],
    editDevice: ['admin', 'Manager', 'sub_admin', 'office_emp'],
    deleteDevice: ['admin', 'sub_admin'],
    viewDevices: ['admin', 'Manager', 'HR', 'office_emp', 'sub_admin'],
  };
  
  return permissions[action]?.includes(role) || false;
};
